import { useQuery } from "@apollo/client";
import { Icon, LoadingSpinner, Tag } from "pepsico-ds";
import { Fragment, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CampaignDetailsModal from "../../../components/common/CampaignDetailsModal/CampaignDetailsModal";
import { GlobalConfigContext } from "../../../context/GlobalConfigContext";
import { findCodeEngagementsById } from "../../../graphql/queries/findCodeEngagementsById";
import { getDateWithFormat } from "../../../utils/commons";
import { FeatureToggle, useFeatureToggle } from "../../../utils/featureToggle";

const CodeInformation = ({ searchText, setCodeGroupId, setLinkedCampaigns }) => {
  const { t } = useTranslation();
  const [expandedRows, setExpandedRows] = useState({});
  const { programTimezone } = useContext(GlobalConfigContext);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const showCampaignShortTitle = useFeatureToggle(
    FeatureToggle.showCampaignShortTitle
  );

  const { data, loading, error } = useQuery(findCodeEngagementsById, {
    variables: { code: searchText },
    onError: (err) => {
      console.error("Error fetching code group data:", err);
    },
  });
  const codeInformation = data?.findCodeEngagementsById ?? [];

  useEffect(() => {
    const results = !loading && !error ? (data?.findCodeEngagementsById ?? []) : [];
    setCodeGroupId(
      results.find((result) => result.codeGroup?.codeGroupId)?.codeGroup
        .codeGroupId || ""
    );
    setLinkedCampaigns?.(
      Array.from(
        new Map(
          results
            .filter((result) => result.campaign?.id)
            .map((result) => [result.campaign.id, result.campaign])
        ).values()
      )
    );
  }, [data, loading, error, setCodeGroupId, setLinkedCampaigns]);

  useEffect(() => {
    setExpandedRows({});
  }, [searchText]);

  const toggleExpand = (index) => {
    setExpandedRows((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const handleCampaignClick = (campaignId) => {
    setSelectedCampaignId(campaignId);
    setShowCampaignModal(true);
  };

  const renderCodeInfoRow = () => {
    if (error) {
      return (
        <tr>
          <td colSpan={9}>{error.graphQLErrors?.[0]?.message || error.message}</td>
        </tr>
      );
    }

    if (!codeInformation?.length) {
      return (
        <tr>
          <td colSpan={9}>{t("CustomerSupport:NoResultsFoundLabel")}</td>
        </tr>
      );
    }

    return codeInformation.map((info, index) => (
      <Fragment key={`entry-${index}`}>
        <tr onClick={() => toggleExpand(index)}>
          <td>
            <Icon
              icon={
                expandedRows[index] ? "keyboard_arrow_up" : "keyboard_arrow_down"
              }
              title={expandedRows[index] ? "Expand less" : "Expand more"}
              size="medium"
            />
          </td>
          <td>
            <Tag
              color={info?.status === "CLAIMED" ? "Green-150" : "Cyan-500"}
              text={
                info?.status === "CLAIMED"
                  ? t("CustomerSupport:UsedLabel")
                  : t("CustomerSupport:UnusedLabel")
              }
              isCopyable={false}
            />
          </td>
          <td>{info?.codeGroup?.basicSetup?.codeGroupName}</td>
          <td>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleCampaignClick(info?.campaign?.id);
              }}
            >
              {info?.campaign?.title || "-"}
            </a>
          </td>
          <td>{showCampaignShortTitle ? info?.campaign?.titleShort || "-" : "-"}</td>
          <td>{info?.profile?.id || "-"}</td>
          <td>
            {info?.profile?.userInfo ? (
              <span>
                {info?.profile?.userInfo?.firstName}{" "}
                {info?.profile?.userInfo?.middleName}{" "}
                {info?.profile?.userInfo?.lastName}
              </span>
            ) : (
              <span>-</span>
            )}
          </td>
          <td>{info?.profile?.userInfo?.emailAddress || "-"}</td>
          <td>{info?.profile?.userInfo?.nationalId || "-"}</td>
        </tr>
        {expandedRows[index] && renderRewardsTable(info)}
      </Fragment>
    ));
  };

  const renderRewardsTable = (info) => {
    if (!info?.rewards?.length > 0) {
      return (
        <tr>
          <td colSpan="8" className="text-center">
            {t("CustomerSupport:NoRewardsFoundLabel")}
          </td>
        </tr>
      );
    }
    return (
      <tr>
        <td colSpan="8" style={{ padding: 0 }}>
          <table style={{ width: "100%" }}>
            <thead>
              <tr>
                <th>{t("CustomerSupport:RewardName")}</th>
                <th>{t("CustomerSupport:AmountUnitColumn")}</th>
                <th>{t("CustomerSupport:QuantityColumn")}</th>
                <th>{t("CustomerSupport:WinDateColumn")}</th>
                <th>{t("CustomerSupport:ExpireDateColumn")}</th>
              </tr>
            </thead>
            <tbody>
              {info?.rewards?.map((reward) => (
                <tr key={reward?.id}>
                  <td>{reward?.name}</td>
                  <td>
                    {reward?.quantity} / {reward?.unitName}
                  </td>
                  <td>{reward?.rewardGroupRewardQuantity}</td>
                  <td>
                    {reward?.createdDate
                      ? getDateWithFormat(reward?.createdDate, programTimezone)
                      : "-"}
                  </td>
                  <td>
                    {reward?.walletWithdrawalDate
                      ? getDateWithFormat(
                          reward?.walletWithdrawalDate,
                          programTimezone
                        )
                      : info?.campaign?.activeEndDate
                        ? getDateWithFormat(
                            info?.campaign?.activeEndDate,
                            programTimezone
                          )
                        : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </td>
      </tr>
    );
  };

  return (
    <div className="list-container">
      {loading ? (
        <LoadingSpinner
          size={{ height: "30px", width: "30px" }}
          style={{ margin: "auto" }}
        />
      ) : (
        <>
          <CampaignDetailsModal
            showModal={showCampaignModal}
            onClose={() => setShowCampaignModal(false)}
            campaignId={selectedCampaignId}
          />
          <table className="list-table">
            <thead>
              <tr>
                <th> </th>
                <th>{t("CustomerSupport:StatusColumn")}</th>
                <th>{t("CustomerSupport:CodeGroupColumn")}</th>
                <th>{t("CustomerSupport:CampaignTitleColumn")}</th>
                <th>{t("CustomerSupport:CampaignTitleShortColumn")}</th>
                <th>{t("CustomerSupport:ProfileIDLabel")}</th>
                <th>{t("CustomerSupport:NameColumn")}</th>
                <th>{t("CustomerSupport:EmailAddressLabel")}</th>
                <th>{t("CustomerSupport:NationalIDLabel")}</th>
              </tr>
            </thead>
            <tbody>{renderCodeInfoRow()}</tbody>
          </table>
        </>
      )}
    </div>
  );
};

export default CodeInformation;
